import React from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography } from '@mui/material';

/**
 * The shared layout for the public content pages.
 *
 * Six pages were carrying an identical copy of the ported template's markup - page-wrapper, wrapper
 * style1, container, content, article - each driving the same template CSS. They are the same page
 * with different words in it, so the layout lives here once and the pages supply only their content.
 *
 * The prose is deliberately left as ordinary HTML rather than being converted to Typography per
 * paragraph. These pages rank in search and the markup is the product; restyling should not mean
 * rewriting several hundred paragraphs and risking the content changing along the way. The theme is
 * applied to those elements through the styles below instead.
 */
export default function ArticlePage(props) {
    const { title, subtitle, image, imageAlt, imageWidth, imageHeight, children, sidebar } = props;

    const article = (
        <>
            <Typography
                variant={'h3'}
                component={'h1'}
                sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    letterSpacing: '-0.025em',
                    marginBottom: { xs: '20px', md: '28px' },
                    fontSize: { xs: '1.9rem', sm: '2.2rem', md: '2.6rem' },
                }}
            >
                {title}
            </Typography>

            {subtitle &&
                <Typography
                    variant={'h6'}
                    component={'p'}
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        marginTop: '-14px',
                        marginBottom: { xs: '20px', md: '28px' },
                    }}
                >
                    {subtitle}
                </Typography>
            }

            {image &&
                <Box
                    component={'img'}
                    src={String(image)}
                    alt={imageAlt}
                    width={imageWidth}
                    height={imageHeight}
                    decoding={'async'}
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        borderRadius: 2,
                        marginBottom: { xs: '24px', md: '36px' },
                    }}
                />
            }

            <Box component={'article'} sx={proseStyles}>
                {children}
            </Box>
        </>
    );

    return (
        <Container maxWidth={'lg'} sx={{ paddingY: { xs: '32px', md: '64px' } }}>
            {sidebar
                ? <Box sx={{
                    display: 'grid',
                    gap: { xs: '32px', md: '56px' },
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
                    alignItems: 'start',
                }}>
                    <Box>{article}</Box>
                    <Box sx={proseStyles}>{sidebar}</Box>
                </Box>
                : article}
        </Container>
    );
}

/**
 * Theme typography applied to the plain HTML the pages are written in.
 *
 * Kept as one object so the public pages share a single reading rhythm, and so a change to how these
 * pages read is made here rather than across nine files.
 */
const proseStyles = {
    color: 'text.primary',

    '& p': {
        fontSize: { xs: '1rem', md: '1.05rem' },
        lineHeight: 1.75,
        marginBottom: '1.4em',
    },

    '& h2': {
        fontSize: { xs: '1.5rem', md: '1.75rem' },
        fontWeight: 700,
        letterSpacing: '-0.025em',
        marginTop: '1.8em',
        marginBottom: '0.6em',
    },

    '& h3': {
        fontSize: { xs: '1.2rem', md: '1.35rem' },
        fontWeight: 700,
        letterSpacing: '-0.015em',
        marginTop: '1.8em',
        marginBottom: '0.5em',
    },

    '& h4': {
        fontSize: '1.05rem',
        fontWeight: 700,
        marginTop: '1.5em',
        marginBottom: '0.4em',
    },

    // The first heading sits directly under the title or image, where the top margin reads as a gap.
    '& > :first-of-type': {
        marginTop: 0,
    },

    '& ul, & ol': {
        paddingLeft: '1.4em',
        marginBottom: '1.4em',
        listStyle: 'revert',
    },

    '& li': {
        fontSize: { xs: '1rem', md: '1.05rem' },
        lineHeight: 1.75,
        marginBottom: '0.4em',
    },

    '& a': {
        color: 'primary.main',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
        '&:hover': {
            color: 'primary.dark',
        },
    },

    '& img': {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 8,
    },

    '& strong, & b': {
        fontWeight: 700,
    },
};

export { proseStyles };

ArticlePage.propTypes = {
    /** Rendered as the page's h1. These pages previously had no h1 at all. */
    title: PropTypes.string.isRequired,

    /** Optional standfirst under the title. */
    subtitle: PropTypes.string,

    /** Optional lead image, imported by the page. */
    image: PropTypes.any,
    imageAlt: PropTypes.string,
    imageWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    imageHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /** The page content, as ordinary HTML. */
    children: PropTypes.node,

    /** Optional column beside the article, used by the contact page. */
    sidebar: PropTypes.node,
};
